import type { LogoutReason } from '@meisterwerk/fe-authentication-sdk'
import { authConnectionProblem, i18tArgs } from '../api/fe-inv-domain-errors'
import { getQueryClient, refetchFailedQueries } from '../api/query-client'
import { type SessionSource, sessionSource } from './embedded-session-source'
import { isEmbeddedPage } from './embedding'
import { browserTimer, RecoveryLoop, type SetTimer } from './recovery-loop'
import {
  type GetTabStorage,
  goToLoginPage,
  type LoginNotice,
  setSignedInMarker,
  takeSignedInMarker,
} from './session-storage-contracts'

export type AuthSessionStatus = 'ok' | 'connection-problem' | 'session-ended'

export interface AuthSessionSnapshot {
  status: AuthSessionStatus
  /** How often a token was acquired again after a connection problem: a key to reset failed views */
  recoveries: number
}

export interface AuthSessionDeps {
  source: Pick<SessionSource, 'isSignedOutHere' | 'freshToken' | 'storedValidToken' | 'storedToken'>
  /** Whether a host app embeds fe-inv; a getter, as the mark of a page of its own is read late */
  isEmbedded: () => boolean
  /** With a notice, the PWA's login page says why the tab came */
  goToLogin: (notice?: LoginNotice) => void
  /** The tab's own storage, for the signed-in marker */
  getStorage: GetTabStorage
  setTimer: SetTimer
  /** Settles once the queries that failed during a connection problem were fetched again */
  refetchFailedQueries: () => Promise<unknown>
}

/**
 * fe-inv's view of the SDK's session (INV-924): a refusal clears the tokens and calls onLogout, any
 * other failed refresh keeps them and calls onRefreshError. A refresh the SDK skips (DPI-271) or
 * drops (DPI-267) calls neither, so a null token reads as no session.
 */
export class AuthSession {
  private snapshot: AuthSessionSnapshot = { status: 'ok', recoveries: 0 }
  private readonly listeners = new Set<() => void>()
  private readonly recovery: RecoveryLoop
  /** Counts onRefreshError calls, so a token getter can tell whether its own call saw one */
  private refreshFailures = 0
  /** A token was acquired again; the failed queries are refetching before the problem lifts */
  private recovering = false

  constructor(private readonly deps: AuthSessionDeps) {
    this.recovery = new RecoveryLoop(deps.setTimer, () => this.tryToRecover())
  }

  getSnapshot = (): AuthSessionSnapshot => this.snapshot

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  /**
   * The token for a request; null once the session ended or never existed. A connection problem
   * throws, and meanwhile a request takes the stored token while valid: a poll or a retry never
   * becomes a retry loop of its own.
   */
  acquireToken = async (): Promise<string | null> => {
    this.resumeIfSignedIn()
    // Ended: the notice offers a sign-in, and a refresh could only be refused again
    if (this.snapshot.status === 'session-ended') return null
    if (this.signedOutHere()) return null
    if (this.snapshot.status === 'connection-problem') return this.storedTokenDuringOutage()
    return await this.freshToken()
  }

  /** For the SDK's onRefreshError: the tokens are kept, the SDK logs the failure to the console */
  handleSdkRefreshError = (): void => {
    this.refreshFailures += 1
    if (this.snapshot.status === 'session-ended') return
    this.setStatus('connection-problem')
    this.recovery.schedule()
  }

  /**
   * For the SDK's onLogout, after it cleared the tokens. Of the PWA's and fe-inv's managers, only
   * the one whose refusal settles first calls it: embedded, fe-inv reports its own refusal.
   */
  handleSdkLogout = (reason: LogoutReason): void => {
    this.recovery.stop()
    const marked = takeSignedInMarker(this.deps.getStorage)
    const hadSession = marked || this.snapshot.status === 'session-ended'
    if (this.deps.isEmbedded()) this.endEmbedded(reason === 'session_ended' && marked, hadSession)
    else if (reason === 'session_ended' && hadSession) this.endSession()
    else this.deps.goToLogin()
  }

  /**
   * Lifts the ended state once a token is stored again: the host signed in without a reload. For
   * the notices, which leave no request to ask for a token, and for a sign-in in another tab.
   */
  resumeIfSignedIn = (): void => {
    if (this.snapshot.status !== 'session-ended') return
    if (this.deps.source.storedToken() === null) return
    setSignedInMarker(this.deps.getStorage)
    this.setStatus('ok')
  }

  goToLogin = (): void => this.deps.goToLogin()

  /** At startup: a token stored, even an expired one, means this tab had a session that can end */
  noteStoredSession = (): void => {
    if (this.deps.source.storedToken() !== null) setSignedInMarker(this.deps.getStorage)
  }

  /** For the host's sign-out, which never reaches this SDK instance's onLogout: no recovery try */
  forgetSignedIn = (): void => {
    takeSignedInMarker(this.deps.getStorage)
    this.liftConnectionProblem()
  }

  /** Each tab refreshes on its own: only a call that saw no failed refresh ends a connection problem */
  private async freshToken(): Promise<string | null> {
    const failuresBefore = this.refreshFailures
    const token = await this.deps.source.freshToken()
    const refreshFailed = this.refreshFailures !== failuresBefore
    if (token) {
      setSignedInMarker(this.deps.getStorage)
      if (!refreshFailed) this.recover()
      return token
    }
    // The SDK drops a refresh answered after the host's sign-out and calls no onLogout: only this
    // sees it, and the sign-out wins over a failed refresh
    if (this.signedOutHere()) return null
    if (refreshFailed) throw this.connectionProblem()
    return null
  }

  private storedTokenDuringOutage(): string {
    const token = this.deps.source.storedValidToken()
    if (!token) throw this.connectionProblem()
    return token
  }

  /** Embedded, an empty shared token set: the host signed out, and nothing is sent or refreshed */
  private signedOutHere(): boolean {
    if (!this.deps.source.isSignedOutHere()) return false
    this.endIf(this.takeHadSession())
    return true
  }

  /**
   * Embedded: fe-inv's own refusal goes to the PWA's login page, with its notice. Any other end is
   * the PWA's, which navigates itself, so fe-inv only ends in place.
   */
  private endEmbedded(refusedHere: boolean, hadSession: boolean) {
    if (!refusedHere) {
      this.endIf(hadSession)
      return
    }
    this.endSession() // nothing fails for want of a token while the page unloads
    this.deps.goToLogin('session_ended')
  }

  /** A tab that had a session shows that it ended (INV-857); any other drops a connection problem */
  private endIf(hadSession: boolean) {
    if (hadSession) this.endSession()
    else this.liftConnectionProblem()
  }

  /** Whether this tab had a session that could end: its marker, which this takes, or one that ended */
  private takeHadSession(): boolean {
    return takeSignedInMarker(this.deps.getStorage) || this.snapshot.status === 'session-ended'
  }

  private endSession() {
    this.recovery.stop()
    this.setStatus('session-ended')
  }

  /** One try of the recovery loop; a failure schedules the next through handleSdkRefreshError */
  private tryToRecover() {
    if (this.snapshot.status !== 'connection-problem') return
    // A sign-out or a refusal cleared the token set the tabs share: a refresh could only sign the
    // browser back in, through the cookie of a sign-out that failed during the outage
    if (this.deps.source.storedToken() === null) {
      this.liftConnectionProblem()
      return
    }
    // No token and no failure: a refusal on the login page, which calls no onLogout, or a refresh
    // the SDK skipped or dropped. The problem is over; the next acquisition decides the session
    this.freshToken().then(
      (token) => {
        if (token === null) this.liftConnectionProblem()
      },
      () => undefined,
    )
  }

  /** Ends a connection problem without a token: the session it kept is over, or decided anew */
  private liftConnectionProblem() {
    this.recovery.stop()
    if (this.snapshot.status === 'connection-problem') this.setStatus('ok')
  }

  /**
   * Refetches what failed, then lifts the problem, which resets the failed views: reset first, they
   * would read the outage's errors again and fail for good. Meanwhile requests take the stored token.
   */
  private recover() {
    if (this.snapshot.status !== 'connection-problem' || this.recovering) return
    this.recovery.stop()
    this.recovering = true
    void this.deps.refetchFailedQueries().finally(() => {
      this.recovering = false
      if (this.snapshot.status !== 'connection-problem') return // it ended meanwhile
      this.setSnapshot({ status: 'ok', recoveries: this.snapshot.recoveries + 1 })
    })
  }

  private setStatus(status: AuthSessionStatus) {
    if (status === this.snapshot.status) return
    this.setSnapshot({ ...this.snapshot, status })
  }

  private setSnapshot(next: AuthSessionSnapshot) {
    this.snapshot = next
    for (const listener of this.listeners) listener()
  }

  private connectionProblem() {
    return authConnectionProblem(
      i18tArgs('platform:error.authConnection'),
      'auth token refresh failed without a refusal',
    )
  }
}

export const authSession = new AuthSession({
  source: sessionSource,
  // On a page of fe-inv's own, the dev harness say, fe-inv's session is the only one
  isEmbedded: isEmbeddedPage,
  goToLogin: goToLoginPage,
  getStorage: () => globalThis.sessionStorage,
  // Read at each call, so that a test can replace the app's timer
  setTimer: (run, ms) => browserTimer.setTimer(run, ms),
  refetchFailedQueries: () => refetchFailedQueries(getQueryClient()),
})
